import styled from '@emotion/styled';
import { motion } from 'motion/react';
import {
  COLORS,
  FONT_FAMILIES,
  FONT_SIZES,
  FONT_WEIGHTS,
  BORDER_RADIUS,
  SPACING,
  Z_INDEX,
  TRANSITIONS,
} from './constants';

// Layout Components
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md};
`;

export const Row = styled.div`
  display: flex;
  gap: ${SPACING.sm};
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${COLORS.border};
  flex-shrink: 0;
`;

// Form Elements
export const Label = styled.label`
  font-size: ${FONT_SIZES.base};
  font-weight: ${FONT_WEIGHTS.medium};
  color: ${COLORS.text};
  font-family: ${FONT_FAMILIES.primary};
  line-height: 140%;
`;

export const Input = styled.input`
  border: 1px solid ${COLORS.border};
  border-radius: ${BORDER_RADIUS.sm};
  padding: ${SPACING.md} ${SPACING.sm};
  font-size: ${FONT_SIZES.md};
  outline: none;
  font-family: ${FONT_FAMILIES.primary};
`;

export const TextArea = styled.textarea`
  border: 1px solid ${COLORS.border};
  border-radius: ${BORDER_RADIUS.sm};
  padding: ${SPACING.md} ${SPACING.sm};
  font-size: ${FONT_SIZES.md};
  min-height: 120px;
  resize: vertical;
  outline: none;
  font-family: ${FONT_FAMILIES.primary};

  /* Custom scrollbar styling to match border-radius */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: ${BORDER_RADIUS.sm};
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: ${BORDER_RADIUS.sm};
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
    background-clip: content-box;
  }

  /* Firefox scrollbar styling */
  scrollbar-width: medium;
  scrollbar-color: #c1c1c1 transparent;
`;

export const FileInput = styled.input`
  display: none;
`;

// Star Rating
export const StarRow = styled.div`
  display: flex;
  gap: ${SPACING.xs};
`;

export const Star = styled.button<{ selected: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Image Upload
export const ImageUploadBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${SPACING.sm};
  width: 100%;
  margin-top: ${SPACING.xs};
  flex-wrap: wrap;
`;

export const Thumbnail = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: ${BORDER_RADIUS.sm};
  overflow: visible;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${BORDER_RADIUS.sm};
`;

export const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 2px;
  background: ${COLORS.backgroundWithBlur};
  border-radius: ${BORDER_RADIUS.full};
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  z-index: 2;
`;

export const UploadArea = styled.label`
  border: 1px dashed ${COLORS.borderDashed};
  border-radius: ${BORDER_RADIUS.sm};
  width: 100%;
  height: 100px;
  background: ${COLORS.background};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${TRANSITIONS.border};
  margin-top: ${SPACING.xs};
  &:hover {
    border-color: ${COLORS.borderHover};
  }
`;

export const SmallUploadArea = styled(UploadArea)`
  width: 100px;
  height: 100px;
  min-width: 100px;
  min-height: 100px;
  margin-top: 0;
`;

// Radio Groups
export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg};
  margin-top: ${SPACING.sm};
`;

export const RadioLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`;

export const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  font-size: ${FONT_SIZES.md};
  color: ${COLORS.text};
  cursor: pointer;
  font-family: ${FONT_FAMILIES.primary};
`;

export const Radio = styled.input`
  accent-color: ${COLORS.primary};
  width: 18px;
  height: 18px;
`;

// Buttons
export const SubmitButton = styled.button<{ disabled: boolean }>`
  background: ${(props) => (props.disabled ? COLORS.primaryDisabled : COLORS.primary)} !important;
  color: ${COLORS.white} !important;
  border: none !important;
  border-radius: ${BORDER_RADIUS.sm} !important;
  font-size: ${FONT_SIZES.sm} !important;
  font-weight: ${FONT_WEIGHTS.normal} !important;
  height: 48px !important;
  width: 100% !important;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')} !important;
  opacity: 1 !important;
  box-shadow: none !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: ${TRANSITIONS.background} !important;
  font-family: ${FONT_FAMILIES.primary} !important;
`;

export const CompleteButton = styled.button`
  background: ${COLORS.primary};
  color: ${COLORS.white};
  border: none;
  border-radius: ${BORDER_RADIUS.sm};
  font-size: ${FONT_SIZES.base};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-family: ${FONT_FAMILIES.primary};
  cursor: pointer;
  box-shadow: none;

  &:hover {
    opacity: 0.9;
  }
`;

// Text Elements
export const GuideText = styled.p`
  font-size: ${FONT_SIZES.xs};
  color: ${COLORS.textSecondary};
  margin: 0;
  font-family: ${FONT_FAMILIES.primary};
`;

export const Optional = styled.span`
  color: ${COLORS.borderDashed};
  font-feature-settings: 'ss10' on;
  font-family: ${FONT_FAMILIES.primary};
  font-size: ${FONT_SIZES.xs};
  font-style: normal;
  font-weight: ${FONT_WEIGHTS.normal};
  line-height: 140%;
`;

// Layout Helpers
export const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
`;

export const BottomButtonRow = styled.div``;

// Terms Section
export const TermsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md};
`;

export const TermsTitle = styled.div`
  color: ${COLORS.primary};
  font-feature-settings: 'ss10' on;
  font-family: ${FONT_FAMILIES.primary};
  font-size: ${FONT_SIZES.xs};
  font-style: normal;
  font-weight: ${FONT_WEIGHTS.normal};
  line-height: 140%;
`;

export const TermsText = styled.div`
  color: ${COLORS.textSecondary};
  font-feature-settings: 'ss10' on;
  font-family: ${FONT_FAMILIES.primary};
  font-size: ${FONT_SIZES.xs};
  font-style: normal;
  font-weight: ${FONT_WEIGHTS.normal};
  line-height: 140%;
`;

// Product Introduction Section
export const IntroSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${SPACING.lg};
  min-height: 136px;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
  flex: 1;
  justify-content: center;
`;

export const IntroText = styled.div`
  color: ${COLORS.text};
  font-family: ${FONT_FAMILIES.primary};
  font-size: ${FONT_SIZES.sm};
  font-style: normal;
  font-weight: ${FONT_WEIGHTS.normal};
  line-height: 140%;
`;

export const IntroSubText = styled.div`
  color: ${COLORS.textSecondary};
  font-feature-settings: 'ss10' on;
  font-family: ${FONT_FAMILIES.primary};
  font-size: ${FONT_SIZES.xs};
  font-style: normal;
  font-weight: ${FONT_WEIGHTS.normal};
  line-height: 140%;
`;

export const ImageContainer = styled.div`
  width: 136px;
  height: 136px;
  background: ${COLORS.backgroundSecondary};
  border-radius: ${SPACING.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

// Complete/Success View
export const CompleteContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg};
`;

export const CompleteTitle = styled.div`
  color: ${COLORS.text};
  font-family: ${FONT_FAMILIES.primary};
  font-weight: ${FONT_WEIGHTS.semibold};
  line-height: 140%;
`;

export const CompleteDesc = styled.div`
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILIES.primary};
  font-weight: ${FONT_WEIGHTS.normal};
  line-height: 140%;
`;

// Modal Overlays
export const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${COLORS.backgroundOverlay};
  z-index: ${Z_INDEX.modal};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ConfirmationOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${COLORS.backgroundOverlayDark};
  z-index: ${Z_INDEX.confirmation};
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Add styled component for required field indicator to eliminate duplication
export const RequiredIndicator = styled.span`
  color: ${COLORS.text};
`;
